import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Phone, Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_findmed-app/artifacts/ge9othd8_LOGO%20SOS%20FINDMED.png";

export default function DigitalCard() {
  const { membershipId } = useParams();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/card/${membershipId}`);
        setCardData(response.data);
      } catch (err) {
        setError('No se pudo cargar la tarjeta digital');
      } finally {
        setLoading(false);
      }
    };

    if (membershipId) {
      fetchCardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipId]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mi Tarjeta SOS FindMed',
          text: `Membresía SOS FindMed - ${cardData.card_code}`,
          url: window.location.href
        });
      } catch (err) {
        toast.info('Copia el enlace de esta página para compartir');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  const handleDownload = () => {
    toast.success('Toma una captura de pantalla para guardar tu tarjeta');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
        <Loader2 className="w-8 h-8 animate-spin text-[#C81D25]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] px-4">
        <Card className="w-full max-w-md border-0 bg-[#1A1A1A]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-8 h-8 text-[#C81D25]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Tarjeta no encontrada</h2>
            <p className="text-gray-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusColors = {
    active: { bg: 'bg-gradient-to-r from-amber-500 to-yellow-500', text: 'text-black' },
    pending: { bg: 'bg-gradient-to-r from-amber-600 to-amber-500', text: 'text-black' },
    consumed: { bg: 'bg-gray-600', text: 'text-white' },
    expired: { bg: 'bg-red-900', text: 'text-white' }
  };

  const statusLabels = {
    active: 'ACTIVA',
    pending: 'PENDIENTE',
    consumed: 'CONSUMIDA',
    expired: 'EXPIRADA'
  };

  const status = statusColors[cardData.status] || statusColors.pending;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <img src={LOGO_URL} alt="SOS FindMed" className="w-48 h-auto mx-auto mb-4" style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }} />
          <h1 className="text-white text-2xl font-light tracking-widest">TARJETA DIGITAL</h1>
        </div>

        {/* Premium Digital Card */}
        <div 
          className="relative rounded-3xl p-8 overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #C81D25 0%, #8B0000 40%, #4A0000 100%)',
            boxShadow: '0 25px 60px rgba(200, 29, 37, 0.5), 0 0 120px rgba(200, 29, 37, 0.15)'
          }}
          data-testid="digital-card"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-yellow-500/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="absolute bottom-4 right-6 text-white/5 text-7xl font-black">SOS</div>
          
          {/* Card content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <img 
                src={LOGO_URL} 
                alt="SOS FindMed" 
                className="w-14 h-14 object-contain drop-shadow-2xl"
              />
              <span className={`${status.bg} ${status.text} px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest`}>
                {statusLabels[cardData.status]}
              </span>
            </div>
            
            {/* Premium chip */}
            <div 
              className="w-14 h-10 rounded-lg mb-6"
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #B8860B 50%, #FFD700 100%)',
                boxShadow: '0 4px 20px rgba(255, 215, 0, 0.4)'
              }}
            />
            
            {/* Card number */}
            <div className="mb-6">
              <p className="text-white/40 text-[10px] mb-2 tracking-[0.3em] uppercase">Número de Membresía</p>
              <p 
                className="text-white text-2xl font-bold tracking-[0.2em] font-mono"
                style={{ textShadow: '0 2px 15px rgba(0,0,0,0.4)' }}
              >
                {cardData.card_code}
              </p>
            </div>
            
            {/* Beneficiary */}
            <div className="mb-6">
              <p className="text-white/40 text-[10px] mb-2 tracking-[0.3em] uppercase">Beneficiario</p>
              <p className="text-white text-xl font-semibold uppercase tracking-wider">
                {cardData.beneficiary_name}
              </p>
            </div>
            
            {/* Golden divider */}
            <div 
              className="h-px my-6"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.4), transparent)' }}
            />
            
            {/* Footer */}
            <div className="flex items-end justify-between">
              <div>
                <p className="text-white/40 text-[9px] mb-1 tracking-[0.2em] uppercase">Vigencia</p>
                <p className="text-white font-medium">
                  {cardData.expiry_date 
                    ? new Date(cardData.expiry_date).toLocaleDateString('es-MX', {
                        year: 'numeric', month: 'short', day: 'numeric'
                      })
                    : 'N/A'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-white/40 text-[9px] mb-1 tracking-[0.2em] uppercase">Emergencias 24/7</p>
                <p 
                  className="text-xl font-black"
                  style={{ 
                    color: '#FFD700',
                    textShadow: '0 0 30px rgba(255,215,0,0.6)'
                  }}
                >
                  {cardData.contact_phone}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button 
            onClick={handleShare}
            className="w-full py-6 text-base font-semibold tracking-wide"
            style={{
              background: 'linear-gradient(135deg, #C81D25, #8B0000)',
              boxShadow: '0 10px 40px rgba(200, 29, 37, 0.4)'
            }}
            data-testid="share-card-btn"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Compartir Tarjeta
          </Button>
          
          <Button 
            onClick={handleDownload}
            variant="outline"
            className="w-full py-6 border-white/20 text-white hover:bg-white/10 tracking-wide"
            data-testid="download-card-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Guardar Captura
          </Button>
        </div>

        {/* Emergency info */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #C81D25, #8B0000)' }}
            >
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold">Línea de Emergencias</p>
              <p className="text-gray-400 text-sm">
                Disponible las 24 horas, los 7 días
              </p>
            </div>
          </div>
          <a 
            href={`tel:${cardData.contact_phone}`}
            className="block mt-4 text-center py-4 rounded-xl text-2xl font-black tracking-wider"
            style={{ 
              color: '#FFD700',
              background: 'rgba(255,215,0,0.1)',
              border: '1px solid rgba(255,215,0,0.2)'
            }}
          >
            {cardData.contact_phone}
          </a>
        </div>

        {/* Back link */}
        <div className="text-center pt-4">
          <a href="/" className="text-gray-500 hover:text-gray-300 text-sm tracking-wide">
            Volver al inicio
          </a>
        </div>
      </div>
    </div>
  );
}
